---
title: 'We built bare-metal Kubernetes for Web3 before the enterprise vendors caught up'
slug: 'bare-metal-kubernetes-web3-before-enterprise'
date: '2026-04-14'
description: 'Nutanix launched bare-metal Kubernetes for enterprise on April 7. Web3 operators ran this in production for years. The reasons are different -- and worth understanding.'
author: 'LinkPool Team'
---

On April 7, Nutanix announced NKP Metal -- bare-metal Kubernetes for enterprise infrastructure. They position it at edge deployments and AI GPU workloads.

For Web3 operators, this is not a new category. It is a description of what production already looks like.

## Why Web3 arrived at bare-metal first

The reason is not preference. It is physics.

An Ethereum validator's attestation window is 12 seconds. Miss it and you lose rewards. Hypervisor overhead adds 0.3 to 1.2ms of latency per hop. On cloud infrastructure, you do not control the hypervisor. You cannot eliminate the overhead.

Chainlink Data Streams operates at block-level timing. A feed needs to land before the next block. That constraint is not negotiable. A burstable instance whose CPU gets throttled mid-delivery is not slow. It is wrong.

RPC endpoints under query load need guaranteed storage I/O. When storage is shared with co-tenants, write latency spikes without warning. Archive nodes for Ethereum need tens of terabytes of sequential reads. On cloud storage, those reads compete with whoever happens to sit next to you.

Platform risk is the third driver. In November 2022, a cloud hosting provider removed more than 1,000 Solana validators from its platform. No migration window. No appeal. The projects running that infrastructure learned that a hosting contract is not the same as infrastructure control.

These are not edge cases. They are the operating conditions for production Web3 infrastructure every day.

## What production looks like

LinkPool runs Chainlink oracle services -- OCR, CCIP, Data Streams, Automation, and Keepers -- alongside Ethereum validators with DVT and RPC endpoints. All of it runs on owned bare-metal servers across three availability zones in Manchester.

The servers are 256-thread, 1.5TB RAM hosts with local NVMe on each machine. The east-west fabric runs at 1.6 Tbps across the three buildings, at sub-0.3ms latency. The cluster is designed so that a single datacenter can go offline and workloads keep running -- etcd quorum is distributed across all three buildings, with redundant network paths across the spine.

Kubernetes runs directly on bare-metal using eBPF networking. No hypervisor. No shared tenancy. No scheduling layer between the workload and the hardware.

The result is deterministic performance. Not performance that is usually good. Consistent by design.

For validators, that means attestation timing that holds under load. For oracle nodes, it means feed delivery that stays inside the block window. For RPC endpoints, it means response times that do not degrade when adjacent workloads spike.

We have operated this infrastructure since 2017. Zero slashing events across all validator deployments.

## The cost gap

The timing argument is often the first one. The cost gap is the one that closes the conversation.

An ETH validator workload on bare-metal costs $97 per month at the annual rate. The same workload on AWS costs $556. A Chainlink node is $36 per month on bare-metal. On AWS, the equivalent spec is $278.

That gap exists because owned hardware carries no cloud margin. The workload pays for compute, not the privilege of using someone else's infrastructure.

DIY bare-metal has its own overhead: engineering time for OS updates, network changes, hardware failures, and on-call rotation. That overhead runs $4,700 to $16,000 per month before a single workload runs. Managed bare-metal removes that cost.

## What the Nutanix announcement actually means

Enterprise infrastructure teams are arriving at the same architectural conclusion that Web3 operators reached under operational pressure. Their drivers are different -- edge latency and GPU density, rather than attestation windows and oracle timing. But the architecture converged from opposite directions.

The significance is practical. The category has a name now. Enterprise buyers are starting to evaluate bare-metal Kubernetes as a deliberate choice, not an unusual constraint. That changes the procurement conversation.

For Web3 teams still running validators or oracle nodes on EKS or GKE: the cost case has not changed. Neither has the performance case. What has changed is that the question will come up in more conversations.

## What moves the decision

Most operators who have run production validators on cloud eventually hit one of three events: an outage that causes missed attestations, a platform policy change that threatens their workload, or a cost review that surfaces the gap.

None of those are hypothetical. All of them have happened to production operators in the last three years.

If you are running steady-state validator or oracle workloads on cloud today, the question is not whether bare-metal performs better. The question is what event changes the calculus.

What has been the forcing function for teams you know who made the switch?
